import { ApolloCache, ApolloLink, Observable } from '@apollo/client';
import type { PossibleTypesMap } from '@apollo/client';
import type { Resolver } from '@apollo/client/core/LocalState';
import type { Resolvers } from '@apollo/client/core/types';
import type { FetchResult, Operation } from '@apollo/client/link/core/types';
import { getCurrentScope } from '@sentry/react';
import axios, { AxiosInstance } from 'axios';
import { compose, identity, isEmpty, mapValues, pick, pickBy, prop } from 'lodash/fp';

import { renameFields } from '@skippay/core/utils/fp';
import { GqlEngine, NetworkError } from '@skippay/core/utils/GqlEngine';
import { insertParams } from '@skippay/core/utils/routeUtils';
import type {
	ApiArtifacts,
	HttpMethod,
	NonBodyArg,
	PossibleType,
} from '@skippay/oas-to-gql-schema/src/types';

export type GqlOperationInfo = Exclude<Parameters<Resolver>[3], undefined>;

export type GroupedVariables = {
	body?: any;
} & { [key in NonBodyArg]?: Record<string, string> };

export type HttpAgentRequest = {
	context: any;
	groupedVariables: GroupedVariables;
	info: GqlOperationInfo;
	method: HttpMethod;
	operationId: string;
	path: string;
};

export type HttpAgent = (request: HttpAgentRequest) => Promise<any>;

const randomToken = () => Math.random().toString(36).substr(2, 9);

const getClientVersion = () => {
	const version = process.env.npm_package_version;
	const system = process.env.BUILD_PLATFORM;

	return `${system || 'web'}/${version || '0.0.0'}`;
};

const groupParameterNamesByType = (
	parameters: Exclude<ApiArtifacts['operations'][0]['parameters'], undefined>
): { [key in NonBodyArg]: string[] } => {
	return parameters.reduce(
		(acc, param) => {
			const group = acc[param.in] || (acc[param.in] = []);
			group.push(param.name);
			return acc;
		},
		{} as ReturnType<typeof groupParameterNamesByType>
	);
};

function uniqPropIn(this: Record<string, unknown>, { uniqProperties }: PossibleType): boolean {
	return uniqProperties.length === 0 || uniqProperties.some(Object.prototype.hasOwnProperty, this);
}
export const resolvePossibleType = (
	possibleTypes: PossibleType[],
	data: Record<string, unknown>
): string => {
	const matchingType = possibleTypes.find(uniqPropIn, data);
	if (matchingType) return matchingType.name;
	throw new Error(`Encountered Union subType which cannot be resolved`);
};

const firstNonArray = (data: any) => (Array.isArray(data) ? firstNonArray(data[0]) : data);

function updateNonArray(this: (item: any) => any, data: any) {
	return Array.isArray(data) ? data.map(updateNonArray, this) : this(data);
}

const createResolvers = (
	{ objectsRelation, objectsRename, operations, possibleTypes }: ApiArtifacts,
	httpAgent: HttpAgent
): Resolvers => {
	const unions = pickBy({ kind: `UNION` }, possibleTypes);
	const resolveUnionTypename = isEmpty(unions)
		? identity
		: (type, data) =>
				unions[type] ? resolvePossibleType(unions[type].possibleTypes, firstNonArray(data)) : type;
	const handleRename = isEmpty(objectsRename)
		? identity
		: (data, typename) =>
				objectsRename[typename] ? renameFields(objectsRename[typename], data) : data;
	const processData = (typename: string) => (data: Record<string, unknown>) => {
		const nonUnionTypename = resolveUnionTypename(typename, data);
		return updateNonArray.call((item) => {
			const renamed = handleRename(item, nonUnionTypename);

			// Fix mutation that return nothing
			if (renamed === '') {
				return {
					__typename: nonUnionTypename,
				};
			}

			// eslint-disable-next-line no-underscore-dangle
			renamed.__typename = nonUnionTypename;
			return renamed;
		}, data);
	};

	const Mutation: Record<string, Resolver> = {};
	const Query: Record<string, Resolver> = {};
	operations.forEach(({ httpMethod, operationId, path, responseType, parameters }) => {
		const rootType = httpMethod === `get` ? Query : Mutation;
		const parameterNamesByType = parameters && groupParameterNamesByType(parameters);
		rootType[operationId] = (rootValue, args, context, info) => {
			const groupedVariables = !args
				? {}
				: {
						body: args?.body,
						...mapValues((parameterNames) => pick(parameterNames, args), parameterNamesByType),
					};
			return httpAgent({
				context,
				groupedVariables,
				info: info!,
				method: httpMethod,
				operationId,
				path,
			}).then(responseType ? processData(responseType) : identity);
		};
	});

	const nonRootResolvers: Resolvers = {};
	Object.entries(objectsRelation).forEach(([typeName, childTypeNames]) => {
		nonRootResolvers[typeName] = Object.entries(childTypeNames).reduce(
			(acc, [fieldName, fieldType]) => {
				acc[fieldName] = (rootValue) =>
					rootValue[fieldName] ? processData(fieldType)(rootValue[fieldName]) : null;
				return acc;
			},
			{} as Record<string, Resolver>
		);
	});

	return { ...nonRootResolvers, Mutation, Query };
};

type GqlExecutionEngine = (operation: Operation) => Promise<FetchResult>;

export const createExecutionEngine = <CacheShape>(
	cache: ApolloCache<CacheShape>,
	httpAgent: HttpAgent,
	apiArtifacts: ApiArtifacts
): GqlExecutionEngine => {
	const gqlEngine = new GqlEngine({ cache, resolvers: createResolvers(apiArtifacts, httpAgent) });
	return function executeOasGqlOperation({ getContext, query, variables }: Operation) {
		return gqlEngine.runResolvers({
			document: query,
			remoteResult: { data: {} },
			context: getContext(),
			variables,
		});
	};
};

export type RequestMiddleware = (next: HttpAgent) => HttpAgent;

export const createAxiosHttpAgent = ({
	axiosInstance = axios,
	baseURL,
	getBaseURL,
	requestMiddlewares = [],
}: {
	axiosInstance?: AxiosInstance;
	baseURL: string;
	getBaseURL?: () => string;
	requestMiddlewares?: RequestMiddleware[];
}): HttpAgent => {
	const axiosAgent: HttpAgent = ({ context, groupedVariables, method, path }) => {
		const transactionId = randomToken();
		const clientVersion = getClientVersion();
		getCurrentScope().setTag('transaction_id', transactionId);

		return axiosInstance({
			baseURL: getBaseURL ? getBaseURL() : baseURL,
			data: groupedVariables.body,
			headers: {
				...(context as any).headers,
				...groupedVariables.header,
				...{
					'X-Transaction-ID': transactionId,
					'x-client-version': clientVersion,
				},
			},
			method,
			params: groupedVariables.query,
			url: groupedVariables.path ? insertParams(path, groupedVariables.path) : path,
		});
	};

	const rethrowNetworkError = (error) => {
		const newError =
			error.isAxiosError && error.response === undefined
				? new NetworkError(error.message, error)
				: error;

		window?.dispatchEvent(new CustomEvent('axios-error', { detail: newError }));

		return Promise.reject(newError);
	};

	const unwrapData = (axiosResponse) => {
		window?.dispatchEvent(new CustomEvent('axios-response', { detail: axiosResponse }));

		return axiosResponse.data;
	};

	const defaultMiddleware: RequestMiddleware = (next) => (request) =>
		next(request).then(unwrapData, rethrowNetworkError);

	return compose(...[defaultMiddleware, ...requestMiddlewares])(axiosAgent);
};

export const createApolloLink = <CacheShape>(
	cache: ApolloCache<CacheShape>,
	apiArtifacts,
	httpAgent: HttpAgent
) => {
	const execute = createExecutionEngine(cache, httpAgent, apiArtifacts);
	return new ApolloLink(
		(operation) =>
			new Observable((observer) => {
				execute(operation).then(
					(result) => {
						if (!observer.closed) {
							observer.next(result);
							observer.complete();
						}
					},
					(error) => {
						if (!observer.closed) {
							observer.error(error);
						}
					}
				);
			})
	);
};

export const possibleTypesFromApiArtifacts: (apiArtifacts: ApiArtifacts) => PossibleTypesMap =
	compose(
		mapValues(({ possibleTypes }) => possibleTypes.map(prop(`name`))),
		prop(`possibleTypes`)
	);
